import { browser } from '$app/environment';
import { cfgOptimoveEnabled } from '@infinitro/config';
export const OPTIMOVE_EVENTS = {
    REGISTRATION: 'registration',
    LOGIN: 'login'
};
const PENDING_AUTH_EVENT_KEY = 'optimove-pending-auth-event';
const PENDING_PAGE_VISIT_KEY = 'optimove-pending-page-visit';
const REPORT_EVENT_TIMEOUT_MS = 1500;
const SDK_FLUSH_POLL_MS = 100;
const SDK_FLUSH_MAX_WAIT_MS = 15000;
let sdkGaveUp = false;
const getOptimoveApi = () => {
    if (!browser || !cfgOptimoveEnabled())
        return undefined;
    const sdk = globalThis.optimoveSDK;
    if (!sdk || typeof sdk !== 'object' || !sdk.API)
        return undefined;
    return sdk.API;
};
const buildEventContext = (extra) => {
    const context = {};
    if (!extra)
        return context;
    for (const [key, value] of Object.entries(extra)) {
        if (value !== undefined) {
            context[key] = value;
        }
    }
    return context;
};
const abandonPendingOptimoveEvents = () => {
    if (!browser)
        return;
    sdkGaveUp = true;
    try {
        sessionStorage.removeItem(PENDING_PAGE_VISIT_KEY);
        sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);
    }
    catch {
        // ignore
    }
    console.log(`optimove SDK did not load within ${SDK_FLUSH_MAX_WAIT_MS}ms, dropped pending events`);
};
const reportOptimoveEvent = (eventName, playerId, parameters) => {
    return new Promise((resolve) => {
        const api = getOptimoveApi();
        if (!api) {
            resolve();
            return;
        }
        let settled = false;
        const finish = () => {
            if (settled)
                return;
            settled = true;
            resolve();
        };
        const timeout = setTimeout(finish, REPORT_EVENT_TIMEOUT_MS);
        try {
            api.setUserId(playerId);
            api.reportEvent(eventName, parameters, () => {
                clearTimeout(timeout);
                finish();
            }, playerId);
        }
        catch (error) {
            clearTimeout(timeout);
            console.log(`optimove ${eventName} event failed: ${error}`);
            finish();
        }
    });
};
export const flushPendingOptimovePageVisit = async () => {
    if (!browser || !cfgOptimoveEnabled() || sdkGaveUp || !getOptimoveApi())
        return;
    let raw;
    try {
        raw = sessionStorage.getItem(PENDING_PAGE_VISIT_KEY);
        if (!raw)
            return;
    }
    catch {
        return;
    }
    try {
        const data = JSON.parse(raw);
        if (!data.url)
            return;
        const api = getOptimoveApi();
        if (!api)
            return;
        api.setPageVisit(data.url, data.title, data.category);
        sessionStorage.removeItem(PENDING_PAGE_VISIT_KEY);
    }
    catch (error) {
        console.log(`optimove pending page visit failed: ${error}`);
    }
};
export const flushPendingOptimoveAuthEvent = async () => {
    if (!browser || !cfgOptimoveEnabled() || !getOptimoveApi())
        return undefined;
    let raw;
    try {
        raw = sessionStorage.getItem(PENDING_AUTH_EVENT_KEY);
        if (!raw)
            return undefined;
    }
    catch {
        return undefined;
    }
    try {
        const data = JSON.parse(raw);
        if (!data.playerId || !data.event)
            return undefined;
        const context = buildEventContext({
            visit_id: data.visitId,
            auth_method: data.authMethod,
            ...(data.event === 'registration' && data.email ? { email: data.email } : {})
        });
        const eventName = data.event === 'login' ? OPTIMOVE_EVENTS.LOGIN : OPTIMOVE_EVENTS.REGISTRATION;
        await reportOptimoveEvent(eventName, data.playerId, context);
        sessionStorage.removeItem(PENDING_AUTH_EVENT_KEY);
        return data.playerId;
    }
    catch (error) {
        console.log(`optimove pending auth event failed: ${error}`);
        return undefined;
    }
};
export const flushPendingOptimoveEvents = async () => {
    await flushPendingOptimovePageVisit();
    return flushPendingOptimoveAuthEvent();
};
export const startOptimoveSdkFlushWatcher = () => {
    if (!browser || !cfgOptimoveEnabled())
        return () => { };
    const tryFlush = () => {
        void flushPendingOptimoveEvents();
    };
    tryFlush();
    const interval = setInterval(tryFlush, SDK_FLUSH_POLL_MS);
    const timeout = setTimeout(() => {
        clearInterval(interval);
        if (!getOptimoveApi()) {
            abandonPendingOptimoveEvents();
        }
    }, SDK_FLUSH_MAX_WAIT_MS);
    return () => {
        clearInterval(interval);
        clearTimeout(timeout);
    };
};
export const setOptimoveUserId = (playerId) => {
    if (!browser || !cfgOptimoveEnabled() || !playerId)
        return;
    const api = getOptimoveApi();
    if (!api)
        return;
    try {
        api.setUserId(playerId);
    }
    catch (error) {
        console.log(`optimove setUserId failed: ${error}`);
    }
};
export const trackOptimovePageVisit = (url, title, category) => {
    if (!browser || !cfgOptimoveEnabled())
        return;
    const api = getOptimoveApi();
    if (api) {
        try {
            api.setPageVisit(url, title, category);
        }
        catch (error) {
            console.log(`optimove setPageVisit failed: ${error}`);
        }
        return;
    }
    if (sdkGaveUp)
        return;
    try {
        sessionStorage.setItem(PENDING_PAGE_VISIT_KEY, JSON.stringify({ url, title, category }));
    }
    catch (error) {
        console.log(`optimove failed to queue page visit: ${error}`);
        return;
    }
    void flushPendingOptimovePageVisit();
};
export const queueOptimoveAuthEvent = (event) => {
    if (!browser || !cfgOptimoveEnabled() || !event.playerId)
        return;
    try {
        sessionStorage.setItem(PENDING_AUTH_EVENT_KEY, JSON.stringify(event));
    }
    catch (error) {
        console.log(`optimove failed to queue auth event: ${error}`);
    }
};
export const trackOptimoveLogin = async (playerId, context) => {
    if (!playerId || !getOptimoveApi())
        return;
    await reportOptimoveEvent(OPTIMOVE_EVENTS.LOGIN, playerId, buildEventContext(context));
};
export const trackOptimoveRegistration = async (playerId, email, context) => {
    if (!playerId || !getOptimoveApi())
        return;
    await reportOptimoveEvent(OPTIMOVE_EVENTS.REGISTRATION, playerId, buildEventContext({ ...context, ...(email ? { email } : {}) }));
};
